import type { Key } from "react-aria-components";

import { Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useListData } from "react-stately";

import { Label } from "@/components/field";
import {
  MultiSelect,
  MultiSelectField,
  MultiSelectItem,
} from "@/components/multi-select";
import { Tag } from "@/components/tag-group";

interface Item {
  id: number | string;
  name: string;
}

type AriaItem = { id: number | string; textValue: string };

interface TagListFieldProps {
  className?: string;
  emptyMessage?: string;
  getItemHref?: (id: number | string) => string;
  isDisabled?: boolean;
  items?: Item[];
  label: string;
  onItemsChange?: (keys: Key[]) => void;
  options?: Item[];
}

function toAriaItem(item: Item): AriaItem {
  return { id: item.id, textValue: item.name };
}

export function TagListField({
  className,
  emptyMessage = "No items",
  getItemHref,
  isDisabled = true,
  items = [],
  label,
  onItemsChange,
  options = [],
}: TagListFieldProps) {
  const ariaOptions = options.map((item) => toAriaItem(item));

  const selectedList = useListData<AriaItem>({
    getKey: (item) => item.id,
    initialItems: [],
  });

  useEffect(() => {
    selectedList.remove(...selectedList.items.map((item) => item.id));
    selectedList.append(...items.map((item) => toAriaItem(item)));
  }, [items]);

  const allItems = [
    ...items.map((item) => toAriaItem(item)),
    ...ariaOptions.filter((opt) => !items.some((item) => item.id === opt.id)),
  ];

  return (
    <MultiSelectField className={className}>
      <Label>{label}</Label>
      <MultiSelect
        isDisabled={isDisabled}
        items={allItems}
        key={allItems.map((i) => i.id).join(",")}
        onItemAdd={(key) => {
          const updatedKeys = [...selectedList.items.map((i) => i.id), key];

          onItemsChange?.(updatedKeys);
        }}
        onItemRemove={(key) => {
          const updatedKeys = selectedList.items
            .map((i) => i.id)
            .filter((id) => id !== key);

          onItemsChange?.(updatedKeys);
        }}
        renderEmptyState={() => (
          <span className="p-2 text-text-tertiary">{emptyMessage}</span>
        )}
        selectedList={selectedList}
        tag={(item) => (
          <Tag isDisabled={isDisabled}>
            {getItemHref ? (
              <Link to={getItemHref(item.id)}>{item.textValue}</Link>
            ) : (
              <span>{item.textValue}</span>
            )}
          </Tag>
        )}
      >
        {(item) => (
          <MultiSelectItem textValue={item.textValue}>
            {item.textValue}
          </MultiSelectItem>
        )}
      </MultiSelect>
    </MultiSelectField>
  );
}
